import React from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ListPatientPageStyled } from './ListPatientPageStyled';
import { Button, Col, Container, Form, Row, Table } from 'react-bootstrap';

import ContainerSideBarComponent from '../../component/container/ContainerSidebarComponent';
import PaginationComponent from '../../component/pagination/PaginationComponent';

import { FaPlus } from 'react-icons/fa';
import { FaSearch } from 'react-icons/fa';
import { FaRegEye } from 'react-icons/fa6';
import { FaTrashAlt } from 'react-icons/fa';

import { InputsSearchPatient, PropsListPatient } from './ListPatientPageType';
import { maskUtils } from '../../../main/helper/maskHelper';
import AlertComponent from '../../component/alert/AlertComponent';
import { FormatDate } from '../../../main/helper/dateFormatter';

/**
 * ListPatientPage
 * @param add
 * @param list
 * @param remove
 * @param edit
 * @param search
 * @param pagination
 * @param toggle
 * @constructor
 */
const ListPatientPage: React.FC<PropsListPatient> = ({ add, list, remove, edit, search, pagination, toggle }: PropsListPatient) => {
  /**
   * useForm
   */
  const { register, getValues, setValue, control } = useForm<InputsSearchPatient>();

  /**
   * Calcula a diferença de idade a partir de uma data fornecida e retorna a string apropriada.
   *
   * @param birthDate - Data de nascimento no formato "MM/DD/YYYY HH:mm:ss".
   * @returns String indicando a diferença de idade com a unidade apropriada.
   */
  function calcularIdade(birthDate: string): string {
    const now = new Date();
    const date = new Date(birthDate);

    if (isNaN(date.getTime())) {
      throw new Error('A data fornecida não é válida.');
    }

    const yearsDiff = now.getFullYear() - date.getFullYear();
    const daysDiff = Math.floor((now.getTime() - date.getTime()) / (1000 * 3600 * 24));
    const hoursDiff = Math.floor((now.getTime() - date.getTime()) / (1000 * 3600));

    if (yearsDiff > 0) {
      return `${yearsDiff} anos`;
    } else if (daysDiff > 0) {
      return `${daysDiff} dias`;
    } else {
      return `${hoursDiff} horas`;
    }
  }

  return (
    <ContainerSideBarComponent>
      <AlertComponent />
      <ListPatientPageStyled>
        <Container fluid className="title-page">
          <h3>Pacientes</h3>
        </Container>

        <Container fluid>
          <Row className="row-field-filter align-items-end">
            <Col xs={12} sm={8} md={6} lg={6} className="mb-3 mb-md-0">
              <Form.Label>Pesquise um paciente pelo nome</Form.Label>
              <Form.Control type="text" placeholder="Pesquise um paciente pelo nome" {...register('nome')} />
            </Col>
            <Col xs={12} sm={4} md={3} lg={3} className="mb-3 mb-md-0">
              <Form.Group>
                <Form.Label>Status</Form.Label>
                <Controller
                  name="status"
                  control={control}
                  defaultValue="Ativos"
                  render={({ field }) => (
                    <Form.Select {...field}>
                      <option value="Ativos">Ativos</option>
                      <option value="Inativos">Inativos</option>
                    </Form.Select>
                  )}
                />
              </Form.Group>
            </Col>

            <Col xs={12} sm={12} md={3} lg={3} className="filter-btn d-flex gap-2 mt-3 mt-md-0">
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() =>
                  search({
                    nome: getValues('nome'),
                    status: getValues('status'),
                  })
                }>
                <FaSearch />
                <span>Pesquisar</span>
              </Button>
              <Button size="sm" onClick={add}>
                <FaPlus />
                <span>Novo</span>
              </Button>
            </Col>
          </Row>
        </Container>
        <Container fluid className="table-page">
          <div style={{ overflowX: 'auto', maxHeight: '600px' }}>
            <Table responsive striped bordered={false} hover style={{ whiteSpace: 'nowrap' }}>
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Celular</th>
                  <th>Email</th>
                  <th>Nascimento</th>
                  <th>Idade</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {list.map((item: any) => (
                  <tr key={item.id} onClick={() => edit(item.id)}>
                    <td>{item.name}</td>
                    <td>{maskUtils.celular(item.cellPhone)}</td>
                    <td>{item.email}</td>
                    <td>{FormatDate.formatToBrazilianDate(item.birthDate)}</td>
                    <td>{calcularIdade(item.birthDate)}</td>
                    <td>
                      <div className="table-btn">
                        <Form.Check
                          type="switch"
                          id="custom-switch"
                          label="Ativo"
                          checked={item.status === 'A'}
                          onChange={async (e) => {
                            if (e.target.checked) {
                              setValue('status', 'Ativos');
                            } else {
                              setValue('status', 'Inativos');
                            }
                            await toggle(item.id);
                          }}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <PaginationComponent
            totalPages={pagination?.totalPages}
            pageIndex={pagination?.pageIndex}
            onClickItem={pagination?.onClickItem}
            hasPreviousPage={pagination?.hasPreviousPage}
            hasNextPage={pagination?.hasNextPage}
          />
        </Container>
      </ListPatientPageStyled>
    </ContainerSideBarComponent>
  );
};

export default ListPatientPage;
